import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, BarChart3, ChevronRight, ArrowRight } from "lucide-react";
import { useAuth } from "@/providers/auth-provider";
import { useBusinesses } from "@/hooks/use-businesses";
import { useSubscriptionPlans } from "@/hooks/use-subscription";
import { toast } from "@/hooks/use-toast";
import { PublicLayout } from "@/components/layout/public-layout";

export default function SubscriptionPlansPage() {
  const { data: plans, isLoading } = useSubscriptionPlans();
  const { user } = useAuth();
  const [, navigate] = useLocation();

  const handleGetStarted = () => {
    if (user) {
      navigate("/subscription");
    } else {
      navigate("/signup");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscription") === "success") {
      toast({ title: "Subscription successful!", description: "Your subscription has been set up." });
    } else if (params.get("subscription") === "cancelled") {
      toast({ title: "Cancelled", description: "You can subscribe anytime.", variant: "destructive" });
    }
  }, []);

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="min-h-[500px] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PublicLayout>
    );
  }

  const features = [
    "Unlimited Transactions",
    "Unlimited Bank Connections",
    "AI-Powered Categorization",
    "Invoices & Estimates",
    "Financial Reports & P&L",
    "Multi-Business Support",
    "Vendor & Customer Management",
    "Plaid Bank Sync",
    "Transaction Splitting",
    "Priority Support",
  ];

  return (
    <PublicLayout>
      <div className="bg-gradient-to-b from-white to-neutral-50">
        {/* Hero */}
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="flex justify-center mb-6">
            <img src="/ZentroBooksLogo.png" alt="ZentroBooks" className="h-16" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-600">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-neutral-600 max-w-2xl mx-auto mb-4">
            One plan. Full access. No hidden fees.
          </p>
          {user && (
            <Button variant="outline" className="gap-2" onClick={() => navigate("/dashboard")}>
              Go to Dashboard <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="container mx-auto px-4 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Trial */}
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="text-2xl">Free Trial</CardTitle>
                <div className="mt-2">
                  <span className="text-3xl font-bold">$0</span>
                  <span className="text-neutral-500"> for 14 days</span>
                </div>
                <CardDescription className="mt-2">
                  Try everything free — no credit card required
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  {["Full access for 14 days", "All features included", "No credit card needed", "Cancel anytime"].map((f, i) => (
                    <li key={i} className="flex">
                      <Check className="h-5 w-5 text-primary shrink-0 mr-2" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="pt-6">
                <Button variant="outline" size="lg" className="w-full" onClick={handleGetStarted}>
                  {user ? "Go to Dashboard" : "Start Free Trial"}
                </Button>
              </CardFooter>
            </Card>

            {/* Paid Plan */}
            <Card className="flex flex-col border-primary shadow-lg ring-1 ring-primary relative">
              <div className="absolute top-0 left-0 right-0 bg-primary py-1.5 text-xs font-semibold text-white text-center rounded-t-lg">
                RECOMMENDED
              </div>
              <CardHeader className="pt-9">
                <CardTitle className="text-2xl">ZentroBooks</CardTitle>
                <div className="mt-2">
                  <span className="text-3xl font-bold">$25</span>
                  <span className="text-neutral-500">/month per entity</span>
                </div>
                <CardDescription className="mt-2">
                  Everything you need to manage your books professionally
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  {features.map((f, i) => (
                    <li key={i} className="flex">
                      <Check className="h-5 w-5 text-primary shrink-0 mr-2" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="pt-6">
                <Button size="lg" className="w-full" onClick={handleGetStarted}>
                  {user ? "Subscribe Now" : "Get Started"} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardFooter>
            </Card>
          </div>

          {/* FAQ-style footer */}
          <div className="max-w-2xl mx-auto mt-16 text-center space-y-4">
            <h3 className="text-lg font-semibold">No surprises. No tiers.</h3>
            <p className="text-muted-foreground">
              Every feature is included in the $25/month plan. Start with a 14-day free trial,
              then subscribe when you're ready. Cancel anytime.
            </p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
